// SPDX-License-Identifier: AGPL-3.0-or-later

import {useMergeRefs} from '@app/features/app/hooks/useMergeRefs';
import styles from '@app/features/ui/components/Slider.module.css';
import {buildMarkerState, getScaledSliderValue} from '@app/features/ui/components/slider/SliderMath';
import {SliderResetControls} from '@app/features/ui/components/slider/SliderResetControls';
import {SliderTooltipPortal} from '@app/features/ui/components/slider/SliderTooltipPortal';
import {SliderTrack} from '@app/features/ui/components/slider/SliderTrack';
import type {SliderProps} from '@app/features/ui/components/slider/SliderTypes';
import {useSliderInteractions} from '@app/features/ui/components/slider/useSliderInteractions';
import {useSliderTooltip} from '@app/features/ui/components/slider/useSliderTooltip';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

export type {SliderProps} from '@app/features/ui/components/slider/SliderTypes';

const RESET_SLIDER_TO_DEFAULT_VALUE_DESCRIPTOR = msg({
	message: 'Reset slider to default value',
	comment: 'Accessible label for a button that resets a slider to its default value.',
});
const SLIDER_VALUE_DESCRIPTOR = msg({
	message: 'Slider value',
	comment: 'Accessible label announcing the current slider value.',
});
export const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
	(
		{
			defaultValue = 10,
			factoryDefaultValue,
			minValue = 0,
			maxValue = 100,
			disabled = false,
			equidistant = false,
			markers,
			className,
			ariaLabel,
			stickToMarkers = false,
			mini = false,
			markerPosition = 'above',
			onValueChange,
			onValueRender,
			onMarkerRender,
			asValueChanges,
			barStyles = {},
			fillStyles = {},
			ariaLabelledBy,
			ariaValueText,
			children,
			value: controlledValue,
			step,
			showResetButton = false,
			onReset,
			resetTooltip,
			resetButtonPlacement = 'inline',
			resetLabel,
			resetAccessory,
		},
		ref,
	) => {
		const {i18n} = useLingui();
		const sliderRef = useRef<HTMLDivElement>(null);
		const thumbRef = useRef<HTMLDivElement>(null);
		const mergedRef = useMergeRefs([ref]);
		const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
		const [newClosestIndex, setNewClosestIndex] = useState<number | null>(null);
		const [showTooltip, setShowTooltip] = useState(false);
		const value = controlledValue !== undefined ? controlledValue : uncontrolledValue;
		const markerState = useMemo(
			() => buildMarkerState({markers, minValue, maxValue, value, equidistant}),
			[equidistant, markers, maxValue, minValue, value],
		);
		const valueScaled = getScaledSliderValue(value, markerState, stickToMarkers, newClosestIndex);
		const notifyLiveValueChange = useCallback(
			(newValue: number) => {
				(asValueChanges ?? onValueChange)?.(newValue);
			},
			[asValueChanges, onValueChange],
		);
		const isAtFactoryDefault = factoryDefaultValue !== undefined && value === factoryDefaultValue;
		const canReset = showResetButton && !isAtFactoryDefault && !disabled;
		const inlineResetLabel = resetTooltip ?? i18n._(RESET_SLIDER_TO_DEFAULT_VALUE_DESCRIPTOR);
		const belowResetLabel = resetLabel ?? resetTooltip ?? i18n._(RESET_SLIDER_TO_DEFAULT_VALUE_DESCRIPTOR);
		const interactions = useSliderInteractions({
			disabled,
			value,
			stickToMarkers,
			equidistant,
			markerState,
			newClosestIndex,
			setNewClosestIndex,
			setUncontrolledValue,
			notifyLiveValueChange,
			onValueChange,
			minValue,
			maxValue,
			step,
			sliderRef,
			setShowTooltip,
		});
		const tooltip = useSliderTooltip({
			showTooltip,
			value,
			isDragging: interactions.isDragging,
			thumbRef,
		});
		const handleReset = useCallback(() => {
			if (disabled || factoryDefaultValue === undefined) return;
			if (onReset) {
				onReset();
			} else {
				setUncontrolledValue(factoryDefaultValue);
				onValueChange?.(factoryDefaultValue);
			}
			sliderRef.current?.focus({preventScroll: true});
		}, [disabled, factoryDefaultValue, onReset, onValueChange]);
		useEffect(() => {
			setUncontrolledValue(defaultValue);
		}, [defaultValue]);
		const track = (
			<SliderTrack
				sliderRef={sliderRef}
				thumbRef={thumbRef}
				markerState={markerState}
				value={value}
				valueScaled={valueScaled}
				factoryDefaultValue={factoryDefaultValue}
				disabled={disabled}
				mini={mini}
				className={className}
				markerPosition={markerPosition}
				barStyles={barStyles}
				fillStyles={fillStyles}
				ariaLabelledBy={ariaLabelledBy}
				ariaValueText={ariaValueText}
				sliderLabel={ariaLabel ?? i18n._(SLIDER_VALUE_DESCRIPTOR)}
				onMarkerRender={onMarkerRender}
				onTrackMouseDown={interactions.handleTrackMouseDown}
				onThumbMouseDown={interactions.handleThumbMouseDown}
				onThumbTouchStart={interactions.handleThumbTouchStart}
				onThumbMouseEnter={() => setShowTooltip(true)}
				onThumbMouseLeave={() => {
					if (!interactions.isDragging) setShowTooltip(false);
				}}
				onKeyDown={interactions.handleKeyDown}
				data-flx="ui.slider.slider-track.key-down"
			>
				{children}
			</SliderTrack>
		);
		const sliderControl = showResetButton ? (
			<SliderResetControls
				placement={resetButtonPlacement}
				canReset={canReset}
				onReset={handleReset}
				inlineLabel={inlineResetLabel}
				label={belowResetLabel}
				accessory={resetAccessory}
				data-flx="ui.slider.slider-reset-controls"
			>
				{track}
			</SliderResetControls>
		) : (
			track
		);
		return (
			<div className={styles.control} ref={mergedRef} data-flx="ui.slider.control">
				{sliderControl}
				<SliderTooltipPortal
					showTooltip={showTooltip}
					shouldRender={!stickToMarkers}
					value={value}
					onValueRender={onValueRender}
					tooltip={tooltip}
					data-flx="ui.slider.slider-tooltip-portal"
				/>
			</div>
		);
	},
);

Slider.displayName = 'Slider';
